import React from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import CustomButton from "./CustomButton";
import { HttpsUrlInputStandalone } from "./HttpsUrlInput";

const DynamicForm = ({
  formTitle,
  formDescription,
  fields,
  validationSchema,
  onSubmit,
  defaultValues,
  isUpdate = false,
  className = "",
  onDelete,
  rightsList = [],
  footerContent = null,
}) => {
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onTouched",
    defaultValues,
  });

  const {
    handleSubmit,
    register,
    setValue,
    control,
    formState: { errors, isDirty },
    watch,
  } = methods;

  const animatedComponents = makeAnimated();

  const selectedRoles = watch("role") || [];

  const isRoleDisabled = (role) => {
    if (selectedRoles.includes("Sponsor Admin")) {
      return role !== "Sponsor Admin";
    }
    if (
      selectedRoles.some((r) =>
        ["RFI Manager", "Library Manager", "VQA Manager"].includes(r)
      )
    ) {
      return role === "Sponsor Admin";
    }
    return false;
  };

  // Restrict input to phone format 1-234-567-8900 and auto-insert dashes
  const handlePhoneInput = (e) => {
    let value = e.target.value.replace(/[^\d]/g, ""); // allow digits only
    if (value.length > 11) value = value.slice(0, 11);
    let formatted = "";
    if (value.length > 0) {
      formatted += value[0];
    }
    if (value.length > 1) {
      formatted += "-" + value.slice(1, 4);
    }
    if (value.length > 4) {
      formatted += "-" + value.slice(4, 7);
    }
    if (value.length > 7) {
      formatted += "-" + value.slice(7, 11);
    }
    e.target.value = formatted;
    // Set value for both phoneNumber and officePhoneNumber if present
    if (e.target.name === "phoneNumber" || e.target.name === "phone") {
      setValue("phoneNumber", formatted);
    }
    if (
      e.target.name === "officePhoneNumber" ||
      e.target.name === "office_phone_number"
    ) {
      setValue("officePhoneNumber", formatted);
    }
  };

  return (
    <div className={`w-full px-5 ${className}`}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-0 md:px-1">
            <h1 className="font-semibold">{formTitle}</h1>
            <p className="text-sm text-gray-600">{formDescription}</p>
          </div>

          {fields?.map((field, index) => (
            <div
              key={index}
              className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6"
            >
              <h1 className="block text-sm font-medium text-gray-900 sm:pt-1.5">
                {field.label}
              </h1>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                {field.type === "textarea" ? (
                  <textarea
                    {...register(field.name)}
                    className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px] h-32 resize-none"
                    placeholder={field.placeholder}
                    defaultValue={defaultValues?.[field.name]}
                  />
                ) : field.type === "creatable-select" ? (
                  <CreatableSelect
                    isMulti
                    name={field.name}
                    options={field.options}
                    className="w-[300px] lg:w-[400px] xl:w-[500px]"
                    classNamePrefix="select"
                    placeholder={field.placeholder}
                    onChange={(val) => {
                      setValue(field.name, val);
                    }}
                    value={watch(field.name)}
                    components={animatedComponents}
                    noOptionsMessage={() => null}
                  />
                ) : field.type === "select" && field.isMulti ? (
                  <Select
                    isMulti
                    name={field.name}
                    options={field.options}
                    className="w-[300px] lg:w-[400px] xl:w-[500px]"
                    classNamePrefix="select"
                    onChange={(val) => {
                      setValue(field.name, val);
                    }}
                    value={watch(field.name)}
                    closeMenuOnSelect={false}
                    components={animatedComponents}
                    styles={{
                      menuList: (base) => ({
                        ...base,
                        maxHeight: "150px",
                        overflowY: "auto",
                      }),
                    }}
                  />
                ) : field.type === "select" ? (
                  <select
                    {...register(field.name)}
                    className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px] [&>option]:bg-white [&>option:hover]:bg-indigo-600 [&>option:hover]:text-white"
                    defaultValue={defaultValues?.[field.name]}
                  >
                    {field.options?.map((option, i) => (
                      <option
                        key={i}
                        value={option.value}
                        className="hover:bg-indigo-600 hover:text-white bg-white text-gray-900"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : field.type === "custom-checkbox" ? (
                  <fieldset className="-space-y-px rounded-md bg-white w-[300px] lg:w-[400px] xl:w-[500px]">
                    {field.options?.map((option, i) => (
                      <label
                        key={option.value}
                        className={`group flex cursor-pointer border border-gray-200 p-4 first:rounded-tl-md first:rounded-tr-md last:rounded-bl-md last:rounded-br-md focus:outline-none ${
                          isRoleDisabled(option.value)
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          {...register(field.name)}
                          value={option.value}
                          defaultChecked={defaultValues?.[field.name]?.includes(
                            option.value
                          )}
                          disabled={isRoleDisabled(option.value)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="ml-3 flex flex-col">
                          <span className="block text-sm font-medium text-gray-900 group-has-[:checked]:text-indigo-900">
                            {option.label}
                          </span>
                          {option.description && (
                            <span className="block text-sm text-gray-500 group-has-[:checked]:text-indigo-700">
                              {option.description}
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </fieldset>
                ) : field.type === "radio" ? (
                  <div className="flex gap-6">
                    {field.options?.map((option, i) => (
                      <label key={i} className="inline-flex items-center">
                        <input
                          type="radio"
                          {...register(field.name)}
                          value={option === "Yes" ? true : false}
                          defaultChecked={
                            defaultValues?.[field.name] === (option === "Yes")
                          }
                          className="form-radio h-4 w-4 text-indigo-600"
                        />
                        <span className="ml-2 text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                ) : field.type === "checkbox" ? (
                  <div className="grid grid-cols-2 gap-6">
                    {field.options?.map((option, i) => (
                      <label
                        key={i}
                        className={`inline-flex items-center ${
                          field.disabled ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          {...register(field.name)}
                          value={option.value}
                          defaultChecked={defaultValues?.[field.name]?.includes(
                            option.value
                          )}
                          disabled={field.disabled}
                          className="form-checkbox h-4 w-4 text-indigo-600"
                        />
                        <span className="ml-2 text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                ) : field.type === "tel" ? (
                  <input
                    type="tel"
                    {...register(field.name)}
                    className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px] px-3 py-2"
                    placeholder={"e.g. 1-234-567-8900"}
                    defaultValue={defaultValues?.[field.name]}
                    onInput={handlePhoneInput}
                    pattern={"^\\d{1}-\\d{3}-\\d{3}-\\d{4}$"}
                    maxLength={14}
                  />
                ) : field.type === "https-url" || field.name === "company_url" ? (
                  <Controller
                    name={field.name}
                    control={control}
                    render={({ field: f }) => (
                      <HttpsUrlInputStandalone
                        name={field.name}
                        label=""
                        placeholder={field.placeholder || "www.vendorwebsite.com"}
                        value={f.value}
                        onChange={f.onChange}
                        onBlur={f.onBlur}
                        errorMessage={errors[field.name]?.message}
                        disabled={field.disabled}
                      />
                    )}
                  />
                ) : (
                  <input
                    type={field.type}
                    {...register(field.name)}
                    className={`${
                      isUpdate && field.name === "email" ? "bg-gray-300" : ""
                    } ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px]`}
                    placeholder={field.placeholder}
                    defaultValue={defaultValues?.[field.name]}
                    disabled={isUpdate && field.name === "email"}
                  />
                )}
                {errors[field.name] && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors[field.name].message}
                  </p>
                )}
              </div>
            </div>
          ))}

          {formTitle === "Add New Sponsor" && rightsList.length > 0 && (
            <div className="mt-6">
              <h2 className="font-semibold text-lg mb-2">
                Rights and Permissions
              </h2>
              <div className="p-4 border border-gray-300 rounded-md shadow-sm space-y-4">
                {rightsList.map((right) => (
                  <div key={right} className="flex items-center">
                    <label className="w-1/3 text-sm font-medium text-gray-900">
                      {right}
                    </label>
                    <div className="w-2/3 flex items-center">
                      <input
                        type="checkbox"
                        {...register(right)}
                        className="mr-2"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Optional slot below the fields, above the button row. Defaults to null so every
              other consumer renders identically. Used for the read-only MFA field (#14476). */}
          {footerContent}

          <div className="mt-10 flex items-center justify-end gap-x-6 py-2 px-5">
            <button
              type="submit"
              disabled={isUpdate && !isDirty}
              className={`inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 
                ${
                  isUpdate && !isDirty
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-500"
                }`}
            >
              {"Save"}
            </button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default DynamicForm;
